import { isKpiQuery } from '@blisspointmedia/bpm-types/dist/querying';
import * as Dfns from 'date-fns/fp';
import {
  AbsoluteDateRange,
  ChartConfig,
  ChartDateRange,
  DateUnit,
  DimensionId,
  KpiId,
  RelativeDateRange,
  resolveKpiControl
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import {
  activeBreakdownFor,
  metricIgnoresKpi,
  withActiveBreakdown
} from '@Components/ChartRenderer/chart-renderer-utils';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { MenuItem } from '@Components/Dropdown';

// ISO "YYYY-MM-DD" — the format both the picker and ChartConfig date ranges use.
const ISO_DATE_FORMAT = 'yyyy-MM-dd';
// What a picker falls back to when there's no range to show: the SAME trailing window a chart's own
// "last 30 days" resolves to, expressed as that range rather than as a separate constant, so a
// dashboard's default and a chart's default can't drift apart again.
const DEFAULT_RANGE: RelativeDateRange = { kind: 'relative', last: 30, unit: 'day' };

// Picker value -> ChartConfig date range. The picker only ever produces concrete dates.
export const toChartDateRange = (range: DateRange): AbsoluteDateRange => ({
  kind: 'absolute',
  start: range.start,
  end: range.end
});

// date-fns/fp subtractors keyed by DateUnit. Mirrors the backend's resolveDateRange
// (bpm-lambdas/dashboarding/queryCompiler.ts) — keep the two in sync so the picker's displayed
// window always equals the window the backend actually queries for a relative range.
const SUB_BY: Record<DateUnit, (date: Date, amount: number) => Date> = {
  day: (date, amount) => Dfns.subDays(amount, date),
  week: (date, amount) => Dfns.subWeeks(amount, date),
  month: (date, amount) => Dfns.subMonths(amount, date),
  quarter: (date, amount) => Dfns.subQuarters(amount, date),
  year: (date, amount) => Dfns.subYears(amount, date)
};

// Seed a picker's displayed window from a chart's saved range, in concrete dates (the picker can
// only display absolute dates). Absolute ranges show their own dates; a relative range is resolved
// the same way the backend does so the displayed window matches what's queried; a missing range
// falls back to DEFAULT_RANGE and resolves through that same path.
export const toPickerRange = (range: ChartDateRange | undefined): DateRange => {
  if (range?.kind === 'absolute') return { start: range.start, end: range.end };

  const relative = range ?? DEFAULT_RANGE;
  // A trailing window ends YESTERDAY: today's data is still accruing, so it's excluded — matching
  // the backend's resolveDateRange and the date picker's own presets. Days are an exact inclusive
  // count (last: 7 => yesterday and the 6 days before it); coarser, variable-length units step back
  // the full `last`, since `last - 1` would collapse `last: 1` to a single day. A compare-period
  // shift then moves both ends back by `by` units.
  const yesterday = Dfns.subDays(1, new Date());
  const amount = relative.unit === 'day' ? relative.last - 1 : relative.last;
  let start = SUB_BY[relative.unit](yesterday, amount);
  let end = yesterday;
  if (relative.shift) {
    start = SUB_BY[relative.shift.unit](start, relative.shift.by);
    end = SUB_BY[relative.shift.unit](end, relative.shift.by);
  }
  return {
    start: Dfns.format(ISO_DATE_FORMAT, start),
    end: Dfns.format(ISO_DATE_FORMAT, end)
  };
};

// Override the date range on every query in a config. Shallow clone — the input is not mutated.
export const applyDateRange = (config: ChartConfig, range: ChartDateRange): ChartConfig => ({
  ...config,
  queries: config.queries.map(q => ({ ...q, dateRange: range }))
});

// Override a period-over-period chart's two windows independently: `comparison` -> the comparison
// query (q1) and a `totalsQueryIds` totals query whose first mapped detail is q1; `primary` -> every
// other query (q0, its totals, a stat's sparkline). Either may be omitted to leave those queries'
// saved windows untouched. Unlike applyDateRange (which overwrites EVERY query with one range,
// collapsing the two periods into one), this keeps the base and comparison distinct. Absolute
// windows override any saved shift. Shallow clone.
export const applyComparisonDateRange = (
  config: ChartConfig,
  primary: ChartDateRange | undefined,
  comparison: ChartDateRange | undefined
): ChartConfig => {
  const totalsQueryIds = config.type === 'table' ? config.encoding.totalsQueryIds : undefined;
  const isComparisonTotals = (totalsId: string) =>
    config.queries.findIndex(d => totalsQueryIds?.[d.queryId] === totalsId) === 1;
  return {
    ...config,
    queries: config.queries.map((q, i) => {
      const range = i === 1 || (i > 1 && isComparisonTotals(q.queryId)) ? comparison : primary;
      return range ? { ...q, dateRange: range } : q;
    })
  };
};

// A config's KPI-using queries.
const getKpiQueries = (config: ChartConfig) => config.queries.filter(isKpiQuery);

// Whether the KPI picker should show. A chart whose KPI-using queries fetch only delivery metrics
// (spend, impressions, CPC) never gets one, not even with a declared `kpi` control: no metric it
// fetches reads the KPI, so switching would change nothing. Checking the queries' `metrics` rather
// than the encoding covers every chart type at once, every metric a metric picker could swap to,
// and every `orderBy` / `metricFilters` term, which must name a selected metric. A chart that
// fetches a KPI-dependent metric alongside delivery ones keeps the picker; MetricPickerChart
// disables it while a delivery metric is the one selected.
//
// Otherwise a declared `kpi` control always wins (a known gap where a declared control sits on a
// fanned chart is tracked separately as DEV-24922). Absent one, only infer the picker when every
// KPI-using query shares the same kpi id — a KPI fan-out chart (one query per KPI, each its own
// line) must not collapse onto a single inferred picker (DEV-24930).
export const usesKpiPicker = (config: ChartConfig): boolean => {
  const kpiQueries = getKpiQueries(config);
  if (kpiQueries.every(q => q.metrics.every(m => metricIgnoresKpi(m.id)))) return false;
  return Boolean(resolveKpiControl(config)) || new Set(kpiQueries.map(q => q.kpi.id)).size === 1;
};

// The KPI a chart's picker defaults to, absent an override: the id on its first KPI-using query.
export const getSavedKpiId = (config: ChartConfig): KpiId | undefined =>
  getKpiQueries(config)[0]?.kpi.id;

// Override the KPI on every KPI-using query in a config (queries that already carry a kpi).
// Queries without a kpi, and per-metric kpi overrides, are left untouched. Shallow clone — the
// input is not mutated. A Retail Metrics query has no kpi, so it is never touched.
export const applyKpi = (config: ChartConfig, kpiId: KpiId): ChartConfig => ({
  ...config,
  queries: config.queries.map(q => (isKpiQuery(q) ? { ...q, kpi: { id: kpiId } } : q))
});

// Narrow a chart's request to ONE breakdown: the viewer's pick when it is still on offer, else the
// control's default. The other breakdown options are dropped from the grouping (withActiveBreakdown),
// so each pick refetches at exactly (x, breakdown). A no-op for a chart without a breakdown control.
// The live selection stays out of config.controls. Shallow clone.
export const applyBreakdown = (config: ChartConfig, pick?: DimensionId): ChartConfig => {
  const active = activeBreakdownFor(config, pick);
  return active ? withActiveBreakdown(config, active) : config;
};

// Build KPI dropdown options from a company's cross-channel KPI names (bare, e.g. "revenue"),
// mapping each to a KpiId-valued MenuItem. If `current` is set and isn't already among the
// company's KPIs (e.g. an agent-set KPI), it's prepended so the chart's active KPI still shows.
export const kpiOptionsFor = (crossChannelKpis: string[], current?: KpiId): MenuItem<KpiId>[] => {
  const options: MenuItem<KpiId>[] = crossChannelKpis.map(name => ({
    value: name,
    label: name
  }));
  if (current && !options.some(o => o.value === current)) {
    options.unshift({ value: current, label: current });
  }
  return options;
};

// Resolve which range applies to a chart: a per-chart override wins, else the global range,
// else undefined (the caller falls back to the chart's own saved range).
export const pickRange = (
  chartId: string,
  perChartRange: Record<string, ChartDateRange>,
  globalRange: ChartDateRange | undefined
): ChartDateRange | undefined => perChartRange[chartId] ?? globalRange;
